/**
 * FissalSuggestion.jsx — renders Fissal's suggested price, Quick Sell / Patient List
 * price tiers, and a hover/focus tooltip explaining the reasoning (issue #97).
 */

import { createSignal, Show, For, onCleanup } from 'solid-js';
import { gold } from '../lib/format.js';
import './FissalSuggestion.css';

const SOURCE_LABELS = {
  redfur_sales:        'Redfur 30d',
  redfur_esohub_blend: 'Redfur + ESO-Hub',
  esohub_sales:        'ESO-Hub PC-NA',
  mm_average:          'MM All-Time',
  ttc_suggested:       'TTC Suggested',
};

export function formatFissalSource(source) {
  return SOURCE_LABELS[source] || source || 'Fissal';
}

export default function FissalSuggestion(props) {
  const f = () => props.fissal;
  const [open, setOpen] = createSignal(false);
  const [tipPos, setTipPos] = createSignal({ left: 0, top: 0 });
  let closeTimer = 0;

  function placeAt(x, y) {
    if (typeof window === 'undefined') return;
    const pad = 10;
    const gapX = 14;
    const gapY = 18;
    const width = Math.min(360, window.innerWidth - pad * 2);
    const height = 240;
    let left = x + gapX;
    let top = y + gapY;
    if (left + width + pad > window.innerWidth) left = x - width - gapX;
    if (top + height + pad > window.innerHeight) top = y - height - gapY;
    left = Math.max(pad, Math.min(window.innerWidth - width - pad, left));
    top = Math.max(pad, Math.min(window.innerHeight - height - pad, top));
    setTipPos({ left, top });
  }

  function showAt(e) {
    clearTimeout(closeTimer);
    placeAt(e.clientX, e.clientY);
    setOpen(true);
  }
  function move(e) {
    if (open()) placeAt(e.clientX, e.clientY);
  }
  function showFocused(e) {
    clearTimeout(closeTimer);
    const r = e.currentTarget.getBoundingClientRect();
    placeAt(r.left + r.width / 2, r.bottom);
    setOpen(true);
  }
  function scheduleHide() {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => setOpen(false), 120);
  }
  onCleanup(() => clearTimeout(closeTimer));

  function handleKey(e) {
    if (e.key === 'Escape') setOpen(false);
  }

  return (
    <Show when={f()?.fissalSuggested}>
      <div class={`v2-fissal-sugg ${props.compact ? 'v2-fissal-sugg--compact' : ''}`}
        onMouseEnter={showAt}
        onMouseMove={move}
        onMouseLeave={scheduleHide}
        onFocusIn={showFocused}
        onFocusOut={scheduleHide}
        onKeyDown={handleKey}
      >
        <button class="v2-fissal-sugg__chip" type="button"
          aria-describedby="v2-fissal-sugg-tip"
          aria-expanded={open()}
          tabIndex={0}>
          <div class="v2-fissal-sugg__main-row">
            <strong>🐱 Fissal suggests</strong>
            <span class="v2-fissal-sugg__price">{gold(f().fissalSuggested)}</span>
            <span class="v2-fissal-sugg__meta">
              <span class="v2-fissal-sugg__src-badge">{formatFissalSource(f().source)}</span>
              <span>· {f().confidence} confidence</span>
              <span class="v2-fissal-sugg__why" aria-hidden="true">ⓘ</span>
            </span>
          </div>
          <Show when={f().quickSell || f().patientList}>
            <div class="v2-fissal-sugg__tiers">
              <Show when={f().quickSell}>
                <span class="v2-fissal-sugg__tier v2-fissal-sugg__tier--quick" title="Quick Sell — competitive undercut band for fast 24–48h turnover.">
                  ⚡ Quick Sell: <b>{gold(f().quickSell)}</b>
                </span>
              </Show>
              <Show when={f().patientList}>
                <span class="v2-fissal-sugg__tier v2-fissal-sugg__tier--patient" title="Patient List — upper realization band for patient kiosk listers.">
                  ⏳ Patient List: <b>{gold(f().patientList)}</b>
                </span>
              </Show>
            </div>
          </Show>
        </button>

        <Show when={open() && (f().reasoning?.text || f().reasoning?.citations?.length)}>
          <div id="v2-fissal-sugg-tip"
            class="v2-fissal-sugg__tip"
            style={{ left: `${tipPos().left}px`, top: `${tipPos().top}px` }}
            role="tooltip"
            onMouseEnter={() => clearTimeout(closeTimer)}
            onMouseLeave={scheduleHide}>
            <div class="v2-fissal-sugg__tip-h">Why this price?</div>
            <Show when={f().reasoning?.text}>
              <p class="v2-fissal-sugg__tip-text">{f().reasoning.text}</p>
            </Show>
            <Show when={(f().reasoning?.citations || []).length}>
              <ul class="v2-fissal-sugg__cites">
                <For each={f().reasoning.citations}>{(c) => (
                  <li>
                    <span class="v2-fissal-sugg__cite-k">{c.label}</span>
                    <span class="v2-fissal-sugg__cite-v">{gold(c.value)}</span>
                  </li>
                )}</For>
              </ul>
            </Show>
            <div class="v2-fissal-sugg__tip-foot">
              source: <code>{formatFissalSource(f().source)}</code> · confidence: <code>{f().confidence}</code>
            </div>
          </div>
        </Show>
      </div>
    </Show>
  );
}
